import React, { useState, useEffect } from "react";
import config from "../../config/config";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

const SliderManager = () => {
  const [sliderItems, setSliderItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    highlight: "",
    subtitle: "",
    description: "",
    button_text: "",
    button_url: "",
    background_image: "",
          // Red Section 1
      red_section_1_title: "",
      red_section_1_description: "",
      red_section_1_image: "",
      red_section_1_url: "",
      // Red Section 2
      red_section_2_title: "",
      red_section_2_description: "",
      red_section_2_image: "",
      red_section_2_url: "",
      // Red Section 3
      red_section_3_title: "",
      red_section_3_description: "",
      red_section_3_image: "",
      red_section_3_url: "",
    is_active: true,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Red section image uploads
  const [redSectionFiles, setRedSectionFiles] = useState({
    red_section_1: null,
    red_section_2: null,
    red_section_3: null
  });
  const [redSectionPreviews, setRedSectionPreviews] = useState({
    red_section_1: null,
    red_section_2: null,
    red_section_3: null
  });
  const [redSectionUploading, setRedSectionUploading] = useState({
    red_section_1: false,
    red_section_2: false,
    red_section_3: false
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchSliderItems();
  }, []);

  const fetchSliderItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("get_slider_items.php"));
      const data = await response.json();

      if (data.success) {
        setSliderItems(data.slider_items || []);
      } else {
        setMessage(
          "Error loading slider items: " + (data.message || "Unknown error")
        );
      }
    } catch (error) {
      setMessage("Error loading slider items: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRedSectionFileSelect = (section, e) => {
    const file = e.target.files[0];
    if (file) {
      setRedSectionFiles(prev => ({ ...prev, [section]: file }));
      const reader = new FileReader();
      reader.onload = (e) => setRedSectionPreviews(prev => ({ ...prev, [section]: e.target.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) return false;

    setUploading(true);
    try {
      console.log("Starting image upload for file:", selectedFile.name); // Debug log
      
      const formData = new FormData();
      formData.append("image", selectedFile);

      console.log("Uploading to:", config.getAdminApiUrl("upload_image.php")); // Debug log

      const response = await fetch(config.getAdminApiUrl("upload_image.php"), {
        method: "POST",
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
        body: formData,
      });

      console.log("Upload response status:", response.status); // Debug log

      const data = await response.json();
      console.log("Upload response data:", data); // Debug log

      if (data.success) {
        setFormData((prev) => ({
          ...prev,
          background_image: data.filename,
        }));
        setMessage("Image uploaded successfully!");
        setTimeout(() => setMessage(""), 3000);
        return true; // Return success
      } else {
        setMessage("Upload failed: " + (data.message || data.error || "Unknown error"));
        return false; // Return failure
      }
    } catch (error) {
      console.error("Upload error:", error); // Debug log
      setMessage("Upload error: " + error.message);
      return false; // Return failure
    } finally {
      setUploading(false);
    }
  };

  const handleRedSectionImageUpload = async (section) => {
    const file = redSectionFiles[section];
    if (!file) return false;

    setRedSectionUploading(prev => ({ ...prev, [section]: true }));
    try {
      console.log(`Starting ${section} image upload for file:`, file.name);
      
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(config.getAdminApiUrl("upload_image.php"), {
        method: "POST",
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        const imageField = `${section}_image`;
        setFormData((prev) => ({
          ...prev,
          [imageField]: data.filename,
        }));
        setMessage(`${section.replace('_', ' ')} image uploaded successfully!`);
        setTimeout(() => setMessage(""), 3000);
        
        // Clear the file and preview
        setRedSectionFiles(prev => ({ ...prev, [section]: null }));
        setRedSectionPreviews(prev => ({ ...prev, [section]: null }));
        
        return true;
      } else {
        setMessage(`Upload failed: ${data.message || data.error || "Unknown error"}`);
        return false;
      }
    } catch (error) {
      console.error("Upload error:", error);
      setMessage(`Upload error: ${error.message}`);
      return false;
    } finally {
      setRedSectionUploading(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleDeleteImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      background_image: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      // If there's a selected file that hasn't been uploaded yet, upload it first
      if (selectedFile && !formData.background_image) {
        setMessage("Uploading image first...");
        const uploadResult = await handleImageUpload();
        if (!uploadResult) {
          setMessage("Failed to upload image. Please try again.");
          setLoading(false);
          return;
        }
      }

      // Ensure background_image is always set for updates
      let finalFormData = { ...formData };
      
      if (editingItem) {
        // For updates, preserve the existing background_image if not changed
        if (!finalFormData.background_image && editingItem.background_image) {
          finalFormData.background_image = editingItem.background_image;
        }
      }

      // Validate required fields
      if (!finalFormData.background_image) {
        setMessage("Error: Background image is required");
        setLoading(false);
        return;
      }

      const url = editingItem
        ? config.getAdminApiUrl("update_slider_item.php")
        : config.getAdminApiUrl("create_slider_item.php");

      const payload = editingItem
        ? { ...finalFormData, id: editingItem.id }
        : finalFormData;

      console.log("Submitting payload:", payload); // Debug log

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(
          editingItem
            ? "Slider item updated successfully!"
            : "Slider item created successfully!"
        );
        setShowModal(false);
        setEditingItem(null);
        resetForm();
        fetchSliderItems();
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("Error: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      setMessage("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    console.log("Editing item:", item); // Debug log
    setEditingItem(item);
    setFormData({
      title: item.title,
      highlight: item.highlight,
      subtitle: item.subtitle,
      description: item.description,
      button_text: item.button_text,
      button_url: item.button_url || "",
      background_image: item.background_image || "",
      // Red Section 1
      red_section_1_title: item.red_section_1_title || "",
      red_section_1_description: item.red_section_1_description || "",
      red_section_1_image: item.red_section_1_image || "",
      red_section_1_url: item.red_section_1_url || "",
      // Red Section 2
      red_section_2_title: item.red_section_2_title || "",
      red_section_2_description: item.red_section_2_description || "",
      red_section_2_image: item.red_section_2_image || "",
      red_section_2_url: item.red_section_2_url || "",
      // Red Section 3
      red_section_3_title: item.red_section_3_title || "",
      red_section_3_description: item.red_section_3_description || "",
      red_section_3_image: item.red_section_3_image || "",
      red_section_3_url: item.red_section_3_url || "",
      is_active: item.is_active === "1" || item.is_active === true,
    });
    setImagePreview(
      item.background_image
        ? config.getImageUrl(item.background_image)
        : null
    );
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this slider item?")) {
      return;
    }

    try {
      setLoading(true);
          const response = await fetch(
      config.getAdminApiUrl("delete_slider_item.php"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setMessage("Slider item deleted successfully!");
        fetchSliderItems();
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("Error: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      setMessage("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      setLoading(true);
      const response = await fetch(
        config.getAdminApiUrl("update_slider_item.php"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "ncg_admin_2024",
          },
          body: JSON.stringify({
            id,
            title: sliderItems.find((item) => item.id === id).title,
            highlight: sliderItems.find((item) => item.id === id).highlight,
            subtitle: sliderItems.find((item) => item.id === id).subtitle,
            description: sliderItems.find((item) => item.id === id).description,
            button_text: sliderItems.find((item) => item.id === id).button_text,
            button_url: sliderItems.find((item) => item.id === id).button_url || "",
            background_image: sliderItems.find((item) => item.id === id)
              .background_image,
            is_active: !currentStatus ? 0 : 1,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setMessage("Status updated successfully!");
        fetchSliderItems();
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("Error: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      setMessage("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSliderItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update display order in database
        const orderData = newItems.map((item, index) => ({
          id: item.id,
          display_order: index + 1,
        }));

        fetch(
          `${
            process.env.NODE_ENV === "development"
              ? "/demo/api/"
              : "/demo/api/"
          }admin/update_slider_order.php`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ items: orderData }),
          }
        ).catch(console.error);

        return newItems;
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      highlight: "",
      subtitle: "",
      description: "",
      button_text: "",
      button_url: "",
      background_image: "",
      // Red Section 1
      red_section_1_title: "",
      red_section_1_description: "",
      red_section_1_image: "",
      red_section_1_url: "",
      // Red Section 2
      red_section_2_title: "",
      red_section_2_description: "",
      red_section_2_image: "",
      red_section_2_url: "",
      // Red Section 3
      red_section_3_title: "",
      red_section_3_description: "",
      red_section_3_image: "",
      red_section_3_url: "",
      is_active: true,
    });
    setSelectedFile(null);
    setImagePreview(null);
    // Clear red section files and previews
    setRedSectionFiles({
      red_section_1: null,
      red_section_2: null,
      red_section_3: null
    });
    setRedSectionPreviews({
      red_section_1: null,
      red_section_2: null,
      red_section_3: null
    });
  };

  const openModal = () => {
    if (sliderItems.length >= 5) {
      setMessage("Maximum 5 slider items allowed!");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    setEditingItem(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    resetForm();
  };

  const SortableSliderItem = ({ item }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id });

    const style = {
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const handleButtonClick = (e) => {
      e.stopPropagation();
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="slider-item"
      >
        <div className="slider-item-content">
          <div className="slider-item-info">
            <div className="slider-order">#{item.display_order}</div>
            <div className="slider-preview">
              <div className="slider-preview-image">
                <img
                  src={config.getImageUrl(item.background_image)}
                  alt="Slider background"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
              <div className="slider-preview-text">
                <h5>
                  {item.title}{" "}
                  <span className="highlight">{item.highlight}</span>{" "}
                  {item.subtitle}
                </h5>
                <p>{item.description}</p>
                <span className="button-text">{item.button_text}</span>
                
                {/* Red Sections Preview */}
                <div className="red-sections-preview" style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff5f5', borderRadius: '6px', border: '1px solid #fed7d7' }}>
                  <h6 style={{ color: '#dc3545', fontSize: '0.9em', marginBottom: '8px', borderBottom: '1px solid #fed7d7', paddingBottom: '5px' }}>
                    <i className="fas fa-square" style={{ color: '#dc3545', marginRight: '5px' }}></i>
                    Red Sections
                  </h6>
                  
                  {/* Red Section 1 */}
                  {item.red_section_1_title && (
                    <div className="red-section-item" style={{ marginBottom: '8px', padding: '5px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.red_section_1_image && (
                          <img 
                            src={config.getImageUrl(item.red_section_1_image)} 
                            alt={item.red_section_1_title}
                            style={{ width: '20px', height: '20px', objectFit: 'cover', borderRadius: '3px' }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <strong style={{ fontSize: '0.8em', color: '#333' }}>{item.red_section_1_title}</strong>
                      </div>
                      {item.red_section_1_description && (
                        <small style={{ color: '#666', fontSize: '0.75em', display: 'block', marginTop: '2px' }}>
                          {item.red_section_1_description.substring(0, 30)}{item.red_section_1_description.length > 30 ? '...' : ''}
                        </small>
                      )}
                      {item.red_section_1_url && (
                        <small style={{ color: '#007bff', fontSize: '0.7em', display: 'block', marginTop: '2px' }}>
                          🔗 {item.red_section_1_url}
                        </small>
                      )}
                    </div>
                  )}
                  
                  {/* Red Section 2 */}
                  {item.red_section_2_title && (
                    <div className="red-section-item" style={{ marginBottom: '8px', padding: '5px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.red_section_2_image && (
                          <img 
                            src={config.getImageUrl(item.red_section_2_image)} 
                            alt={item.red_section_2_title}
                            style={{ width: '20px', height: '20px', objectFit: 'cover', borderRadius: '3px' }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <strong style={{ fontSize: '0.8em', color: '#333' }}>{item.red_section_2_title}</strong>
                      </div>
                      {item.red_section_2_description && (
                        <small style={{ color: '#666', fontSize: '0.75em', display: 'block', marginTop: '2px' }}>
                          {item.red_section_2_description.substring(0, 30)}{item.red_section_2_description.length > 30 ? '...' : ''}
                        </small>
                      )}
                      {item.red_section_2_url && (
                        <small style={{ color: '#007bff', fontSize: '0.7em', display: 'block', marginTop: '2px' }}>
                          🔗 {item.red_section_2_url}
                        </small>
                      )}
                    </div>
                  )}
                  
                  {/* Red Section 3 */}
                  {item.red_section_3_title && (
                    <div className="red-section-item" style={{ marginBottom: '8px', padding: '5px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.red_section_3_image && (
                          <img 
                            src={config.getImageUrl(item.red_section_3_image)} 
                            alt={item.red_section_3_title}
                            style={{ width: '20px', height: '20px', objectFit: 'cover', borderRadius: '3px' }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <strong style={{ fontSize: '0.8em', color: '#333' }}>{item.red_section_3_title}</strong>
                      </div>
                      {item.red_section_3_description && (
                        <small style={{ color: '#666', fontSize: '0.75em', display: 'block', marginTop: '2px' }}>
                          {item.red_section_3_description.substring(0, 30)}{item.red_section_3_description.length > 30 ? '...' : ''}
                        </small>
                      )}
                      {item.red_section_3_url && (
                        <small style={{ color: '#007bff', fontSize: '0.7em', display: 'block', marginTop: '2px' }}>
                          🔗 {item.red_section_3_url}
                        </small>
                      )}
                    </div>
                  )}
                  
                  {/* Show message if no red sections */}
                  {!item.red_section_1_title && !item.red_section_2_title && !item.red_section_3_title && (
                    <small style={{ color: '#999', fontStyle: 'italic' }}>
                      No red sections configured
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="slider-item-actions" onClick={handleButtonClick}>
            <button
              className={`btn btn-sm ${
                item.is_active === "1" || item.is_active === true
                  ? "btn-success"
                  : "btn-secondary"
              }`}
              onClick={() =>
                handleToggleActive(
                  item.id,
                  item.is_active === "1" || item.is_active === true
                )
              }
            >
              {item.is_active === "1" || item.is_active === true
                ? "Active"
                : "Inactive"}
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => handleEdit(item)}
            >
              <i className="fas fa-edit"></i> Edit
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => handleDelete(item.id)}
            >
              <i className="fas fa-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && sliderItems.length === 0) {
    return (
      <div className="page-manager">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading slider items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-manager">
      <div className="manager-header">
        <div className="header-left">
          <h2>Slider Management</h2>
          <p>Manage homepage slider content and images</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={openModal}>
            <i className="fas fa-plus me-2"></i>
            Add Slider Item
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`alert ${
            message.includes("Error") ? "alert-danger" : "alert-success"
          }`}
        >
          {message}
        </div>
      )}

      <div className="manager-content">
        {sliderItems.length === 0 ? (
          <div className="no-data">
            <i className="fas fa-images"></i>
            <p>
              No slider items found. Add your first slider item to get started.
            </p>
          </div>
        ) : (
          <div className="slider-items-container">
            <div className="slider-info">
              <p>
                <strong>{sliderItems.length}/5</strong> slider items • Drag to
                reorder
              </p>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sliderItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {sliderItems.map((item) => (
                  <SortableSliderItem key={item.id} item={item} />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-content large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>
                {editingItem ? "Edit Slider Item" : "Add New Slider Item"}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Explore India's"
                    />
                  </div>
                  <div className="form-group">
                    <label>Highlight *</label>
                    <input
                      type="text"
                      name="highlight"
                      value={formData.highlight}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Manufacturing"
                    />
                  </div>
                  <div className="form-group">
                    <label>Subtitle *</label>
                    <input
                      type="text"
                      name="subtitle"
                      value={formData.subtitle}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Competence"
                    />
                  </div>
                  <div className="form-group">
                    <label>Button Text *</label>
                    <input
                      type="text"
                      name="button_text"
                      value={formData.button_text}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Know More"
                    />
                  </div>
                  <div className="form-group">
                    <label>Button URL</label>
                    <input
                      type="url"
                      name="button_url"
                      value={formData.button_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com/page or /about-us"
                    />
                    <small>Leave empty if button should not link anywhere</small>
                  </div>
                  
                  {/* Red Section 1 */}
                  <div className="form-group full-width">
                    <label className="section-label" style={{ 
                      color: '#dc3545', 
                      fontWeight: 'bold', 
                      fontSize: '1.1em',
                      borderBottom: '2px solid #dc3545',
                      paddingBottom: '5px',
                      marginBottom: '15px'
                    }}>
                      <i className="fas fa-square" style={{ color: '#dc3545', marginRight: '8px' }}></i>
                      Red Section 1
                    </label>
                    <div className="red-section-fields" style={{
                      backgroundColor: '#fff5f5',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #fed7d7'
                    }}>
                      <div className="form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          name="red_section_1_title"
                          value={formData.red_section_1_title}
                          onChange={handleInputChange}
                          placeholder="e.g., SCM & Retail Network"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          name="red_section_1_description"
                          value={formData.red_section_1_description}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="Enter description for red section 1..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Image</label>
                        
                        {/* Current Image Display */}
                        {formData.red_section_1_image && !redSectionFiles.red_section_1 && (
                          <div className="current-image" style={{ marginBottom: '10px' }}>
                            <img
                              src={config.getImageUrl(formData.red_section_1_image)}
                              alt="Current red section 1 image"
                              style={{
                                maxWidth: "150px",
                                maxHeight: "90px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "2px solid #fed7d7"
                              }}
                            />
                            <div className="image-actions" style={{ marginTop: '5px' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => setFormData(prev => ({ ...prev, red_section_1_image: "" }))}
                              >
                                <i className="fas fa-trash"></i> Remove
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Image Preview */}
                        {redSectionPreviews.red_section_1 && redSectionFiles.red_section_1 && (
                          <div className="image-preview" style={{ marginBottom: '10px' }}>
                            <img
                              src={redSectionPreviews.red_section_1}
                              alt="Preview"
                              style={{
                                maxWidth: "150px",
                                maxHeight: "90px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "2px solid #fed7d7"
                              }}
                            />
                            <div className="image-actions" style={{ marginTop: '5px' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => handleRedSectionImageUpload('red_section_1')}
                                disabled={redSectionUploading.red_section_1}
                              >
                                {redSectionUploading.red_section_1 ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin"></i> Uploading...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-check"></i> Upload Image
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setRedSectionFiles(prev => ({ ...prev, red_section_1: null }));
                                  setRedSectionPreviews(prev => ({ ...prev, red_section_1: null }));
                                }}
                              >
                                <i className="fas fa-times"></i> Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* File Input */}
                        {!formData.red_section_1_image && !redSectionFiles.red_section_1 && (
                          <div className="file-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleRedSectionFileSelect('red_section_1', e)}
                              style={{ display: "none" }}
                              id="red-section-1-upload"
                            />
                            <label
                              htmlFor="red-section-1-upload"
                              className="file-upload-label"
                              style={{
                                display: "block",
                                padding: "10px",
                                border: "2px dashed #fed7d7",
                                borderRadius: "6px",
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: "#fff5f5"
                              }}
                            >
                              <i className="fas fa-cloud-upload-alt" style={{ color: '#dc3545', fontSize: '1.2em' }}></i>
                              <span style={{ display: 'block', marginTop: '5px' }}>Choose Image</span>
                              <small style={{ color: '#666' }}>JPG, PNG, GIF (Max 5MB)</small>
                            </label>
                          </div>
                        )}

                        {/* Upload New Image Button */}
                        {formData.red_section_1_image && !redSectionFiles.red_section_1 && (
                          <div className="upload-new">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleRedSectionFileSelect('red_section_1', e)}
                              style={{ display: "none" }}
                              id="red-section-1-upload-new"
                            />
                            <label
                              htmlFor="red-section-1-upload-new"
                              className="btn btn-outline-primary btn-sm"
                            >
                              <i className="fas fa-image"></i> Select New Image
                            </label>
                          </div>
                        )}

                        <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                          <strong>Upload Process:</strong> 1) Select image → 2) Click "Upload Image" → 3) Image automatically saved
                        </small>
                      </div>
                      
                      {/* URL Field */}
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="text"
                          name="red_section_1_url"
                          value={formData.red_section_1_url}
                          onChange={handleInputChange}
                          placeholder="e.g., /services, /about-us, or full URL"
                        />
                        <small>Leave empty if no navigation needed</small>
                      </div>
                    </div>
                  </div>
                  
                  {/* Red Section 2 */}
                  <div className="form-group full-width">
                    <label className="section-label" style={{ 
                      color: '#dc3545', 
                      fontWeight: 'bold', 
                      fontSize: '1.1em',
                      borderBottom: '2px solid #dc3545',
                      paddingBottom: '5px',
                      marginBottom: '15px'
                    }}>
                      <i className="fas fa-square" style={{ color: '#dc3545', marginRight: '8px' }}></i>
                      Red Section 2
                    </label>
                    <div className="red-section-fields" style={{
                      backgroundColor: '#fff5f5',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #fed7d7'
                    }}>
                      <div className="form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          name="red_section_2_title"
                          value={formData.red_section_2_title}
                          onChange={handleInputChange}
                          placeholder="e.g., Procurement Outsourcing Services"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          name="red_section_2_description"
                          value={formData.red_section_2_description}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="Enter description for red section 2..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Image</label>
                        
                        {/* Current Image Display */}
                        {formData.red_section_2_image && !redSectionFiles.red_section_2 && (
                          <div className="current-image" style={{ marginBottom: '10px' }}>
                            <img
                              src={config.getImageUrl(formData.red_section_2_image)}
                              alt="Current red section 2 image"
                              style={{
                                maxWidth: "150px",
                                maxHeight: "90px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "2px solid #fed7d7"
                              }}
                            />
                            <div className="image-actions" style={{ marginTop: '5px' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => setFormData(prev => ({ ...prev, red_section_2_image: "" }))}
                              >
                                <i className="fas fa-trash"></i> Remove
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Image Preview */}
                        {redSectionPreviews.red_section_2 && redSectionFiles.red_section_2 && (
                          <div className="image-preview" style={{ marginBottom: '10px' }}>
                            <img
                              src={redSectionPreviews.red_section_2}
                              alt="Preview"
                              style={{
                                maxWidth: "150px",
                                maxHeight: "90px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "2px solid #fed7d7"
                              }}
                            />
                            <div className="image-actions" style={{ marginTop: '5px' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => handleRedSectionImageUpload('red_section_2')}
                                disabled={redSectionUploading.red_section_2}
                              >
                                {redSectionUploading.red_section_2 ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin"></i> Uploading...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-check"></i> Upload Image
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setRedSectionFiles(prev => ({ ...prev, red_section_2: null }));
                                  setRedSectionPreviews(prev => ({ ...prev, red_section_2: null }));
                                }}
                              >
                                <i className="fas fa-times"></i> Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* File Input */}
                        {!formData.red_section_2_image && !redSectionFiles.red_section_2 && (
                          <div className="file-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleRedSectionFileSelect('red_section_2', e)}
                              style={{ display: "none" }}
                              id="red-section-2-upload"
                            />
                            <label
                              htmlFor="red-section-2-upload"
                              className="file-upload-label"
                              style={{
                                display: "block",
                                padding: "10px",
                                border: "2px dashed #fed7d7",
                                borderRadius: "6px",
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: "#fff5f5"
                              }}
                            >
                              <i className="fas fa-cloud-upload-alt" style={{ color: '#dc3545', fontSize: '1.2em' }}></i>
                              <span style={{ display: 'block', marginTop: '5px' }}>Choose Image</span>
                              <small style={{ color: '#666' }}>JPG, PNG, GIF (Max 5MB)</small>
                            </label>
                          </div>
                        )}

                        {/* Upload New Image Button */}
                        {formData.red_section_2_image && !redSectionFiles.red_section_2 && (
                          <div className="upload-new">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleRedSectionFileSelect('red_section_2', e)}
                              style={{ display: "none" }}
                              id="red-section-2-upload-new"
                            />
                            <label
                              htmlFor="red-section-2-upload-new"
                              className="btn btn-outline-primary btn-sm"
                            >
                              <i className="fas fa-image"></i> Select New Image
                            </label>
                          </div>
                        )}

                        <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                          <strong>Upload Process:</strong> 1) Select image → 2) Click "Upload Image" → 3) Image automatically saved
                        </small>
                      </div>
                      
                      {/* URL Field */}
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="text"
                          name="red_section_2_url"
                          value={formData.red_section_2_url}
                          onChange={handleInputChange}
                          placeholder="e.g., /services, /about-us, or full URL"
                        />
                        <small>Leave empty if no navigation needed</small>
                      </div>
                    </div>
                  </div>
                  
                  {/* Red Section 3 */}
                  <div className="form-group full-width">
                    <label className="section-label" style={{ 
                      color: '#dc3545', 
                      fontWeight: 'bold', 
                      fontSize: '1.1em',
                      borderBottom: '2px solid #dc3545',
                      paddingBottom: '5px',
                      marginBottom: '15px'
                    }}>
                      <i className="fas fa-square" style={{ color: '#dc3545', marginRight: '8px' }}></i>
                      Red Section 3
                    </label>
                    <div className="red-section-fields" style={{
                      backgroundColor: '#fff5f5',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #fed7d7'
                    }}>
                      <div className="form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          name="red_section_3_title"
                          value={formData.red_section_3_title}
                          onChange={handleInputChange}
                          placeholder="e.g., Engineering Design Services"
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          name="red_section_3_description"
                          value={formData.red_section_3_description}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="Enter description for red section 3..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Image</label>
                        
                        {/* Current Image Display */}
                        {formData.red_section_3_image && !redSectionFiles.red_section_3 && (
                          <div className="current-image" style={{ marginBottom: '10px' }}>
                            <img
                              src={config.getImageUrl(formData.red_section_3_image)}
                              alt="Current red section 3 image"
                              style={{
                                maxWidth: "150px",
                                maxHeight: "90px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "2px solid #fed7d7"
                              }}
                            />
                            <div className="image-actions" style={{ marginTop: '5px' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => setFormData(prev => ({ ...prev, red_section_3_image: "" }))}
                              >
                                <i className="fas fa-trash"></i> Remove
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Image Preview */}
                        {redSectionPreviews.red_section_3 && redSectionFiles.red_section_3 && (
                          <div className="image-preview" style={{ marginBottom: '10px' }}>
                            <img
                              src={redSectionPreviews.red_section_3}
                              alt="Preview"
                              style={{
                                maxWidth: "150px",
                                maxHeight: "90px",
                                objectFit: "cover",
                                borderRadius: "6px",
                                border: "2px solid #fed7d7"
                              }}
                            />
                            <div className="image-actions" style={{ marginTop: '5px' }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => handleRedSectionImageUpload('red_section_3')}
                                disabled={redSectionUploading.red_section_3}
                              >
                                {redSectionUploading.red_section_3 ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin"></i> Uploading...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-check"></i> Upload Image
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setRedSectionFiles(prev => ({ ...prev, red_section_3: null }));
                                  setRedSectionPreviews(prev => ({ ...prev, red_section_3: null }));
                                }}
                              >
                                <i className="fas fa-times"></i> Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* File Input */}
                        {!formData.red_section_3_image && !redSectionFiles.red_section_3 && (
                          <div className="file-upload-area">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleRedSectionFileSelect('red_section_3', e)}
                              style={{ display: "none" }}
                              id="red-section-3-upload"
                            />
                            <label
                              htmlFor="red-section-3-upload"
                              className="file-upload-label"
                              style={{
                                display: "block",
                                padding: "10px",
                                border: "2px dashed #fed7d7",
                                borderRadius: "6px",
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: "#fff5f5"
                              }}
                            >
                              <i className="fas fa-cloud-upload-alt" style={{ color: '#dc3545', fontSize: '1.2em' }}></i>
                              <span style={{ display: 'block', marginTop: '5px' }}>Choose Image</span>
                              <small style={{ color: '#666' }}>JPG, PNG, GIF (Max 5MB)</small>
                            </label>
                          </div>
                        )}

                        {/* Upload New Image Button */}
                        {formData.red_section_3_image && !redSectionFiles.red_section_3 && (
                          <div className="upload-new">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleRedSectionFileSelect('red_section_3', e)}
                              style={{ display: "none" }}
                              id="red-section-3-upload-new"
                            />
                            <label
                              htmlFor="red-section-3-upload-new"
                              className="btn btn-outline-primary btn-sm"
                            >
                              <i className="fas fa-image"></i> Select New Image
                            </label>
                          </div>
                        )}

                        <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                          <strong>Upload Process:</strong> 1) Select image → 2) Click "Upload Image" → 3) Image automatically saved
                        </small>
                      </div>
                      
                      {/* URL Field */}
                      <div className="form-group">
                        <label>URL</label>
                        <input
                          type="text"
                          name="red_section_3_url"
                          value={formData.red_section_3_url}
                          onChange={handleInputChange}
                          placeholder="e.g., /services, /about-us, or full URL"
                        />
                        <small>Leave empty if no navigation needed</small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group full-width">
                    <label>Description *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows="3"
                      placeholder="Enter slider description..."
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Background Image *</label>

                    {/* Current Image Display */}
                    {formData.background_image && !selectedFile && (
                      <div className="current-image">
                        <img
                          src={config.getImageUrl(formData.background_image)}
                          alt="Current background"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "120px",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                        <div className="image-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={handleDeleteImage}
                          >
                            <i className="fas fa-trash"></i> Remove
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Image Preview */}
                    {imagePreview && selectedFile && (
                      <div className="image-preview">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "120px",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                        <div className="image-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={handleImageUpload}
                            disabled={uploading}
                          >
                            {uploading ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i>{" "}
                                Setting...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-check"></i> Set Image
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={handleDeleteImage}
                          >
                            <i className="fas fa-times"></i> Cancel
                          </button>
                        </div>
                        
                        {/* Warning if image not uploaded yet */}
                        {!formData.background_image && (
                          <div className="alert alert-warning mt-2" style={{ fontSize: '0.8rem' }}>
                            <i className="fas fa-exclamation-triangle"></i>
                            <strong>Important:</strong> Click "Set Image" to upload this file before saving!
                          </div>
                        )}
                      </div>
                    )}

                    {/* File Input */}
                    {!formData.background_image && !selectedFile && (
                      <div className="file-upload-area">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          style={{ display: "none" }}
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="file-upload-label"
                        >
                          <i className="fas fa-cloud-upload-alt"></i>
                          <span>Choose an image file</span>
                          <small>
                            Click to select an image (JPG, PNG, GIF)
                          </small>
                        </label>
                      </div>
                    )}

                    {/* Upload New Image Button */}
                    {formData.background_image && !selectedFile && (
                      <div className="upload-new">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          style={{ display: "none" }}
                          id="image-upload-new"
                        />
                        <label
                          htmlFor="image-upload-new"
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="fas fa-image"></i> Select New Image
                        </label>
                      </div>
                    )}

                    <small>
                      <strong>Upload Process:</strong> 1) Select image file → 2) Click "Set Image" to upload → 3) Save slider item. 
                      The image will be automatically uploaded to the server.
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      Active
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      {editingItem ? "Updating..." : "Creating..."}
                    </>
                  ) : editingItem ? (
                    "Update Slider Item"
                  ) : (
                    "Create Slider Item"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SliderManager;
